/**
 * Защита боевых отчётов от данных с чужих окружений.
 *
 * Локальный фронт умеет смотреть на прод: в `.env` рядом с localhost лежат
 * закомментированные боевые адреса, и переключение — одна строка. С включённым
 * приёмником каждый клик разработчика уезжал бы в прод-таблицу событий, где он
 * неотличим от гостя: institution_id тот же, пометки окружения у события нет.
 *
 * На CORS полагаться нельзя: `http://localhost:3000` разрешён и в проде (нужен
 * мобильным сборкам), а пачка из `sendBeacon` уходит как simple request, который
 * preflight вообще не проходит — браузер её отправит, что бы ни ответил сервер.
 */

/** Имена, которыми машина зовёт саму себя. */
const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '::1', '[::1]', '0.0.0.0'])

/** Адрес из приватной сети (RFC 1918) или link-local — типовой dev-сервер в LAN. */
const isPrivateIp = (hostname: string): boolean => {
  const parts = hostname.split('.')
  if (parts.length !== 4) return false

  const [a, b] = parts.map(Number)
  if (parts.some((part) => !/^\d{1,3}$/.test(part)) || a > 255 || b > 255) return false

  if (a === 10) return true
  if (a === 192 && b === 168) return true
  if (a === 172 && b >= 16 && b <= 31) return true
  return a === 169 && b === 254
}

/** Хост машины разработчика: localhost, петля или приватная сеть. */
export const isLocalHostname = (hostname: string): boolean =>
  LOOPBACK_HOSTS.has(hostname) || isPrivateIp(hostname)

/**
 * Страница открыта на машине разработчика, а API — чужой (прод или стейдж).
 * Ровно тот случай, когда события уехали бы не в свою базу.
 *
 * Мобильные сборки под это не попадают: WebView Capacitor работает с
 * `capacitor://localhost` и всегда ходит на удалённый API — их отсекаем по схеме.
 */
export const isCrossEnvironmentTarget = (pageOrigin: string, apiBaseUrl: string): boolean => {
  if (!pageOrigin || !apiBaseUrl) return false

  try {
    const page = new URL(pageOrigin)
    // Нативная сборка: схема не http(s) либо localhost без порта — это не dev-сервер.
    if (page.protocol !== 'http:' && page.protocol !== 'https:') return false
    if (!isLocalHostname(page.hostname)) return false
    if (!page.port) return false

    const api = new URL(apiBaseUrl, pageOrigin)
    return !isLocalHostname(api.hostname)
  } catch {
    // Разобрать не смогли — не мешаем работе, это всего лишь предохранитель.
    return false
  }
}
